import React, { useState } from 'react';
import styled from 'styled-components';
import { useApartments } from '../context/ApartmentContext';
import ApartmentDetailsManager from '../components/ApartmentDetailsManager';
import { AlertTriangle } from 'lucide-react';
import { ApartmentStatus } from '../types/Apartment';
import { formatPrice } from '../config/priceConfig';

// Admin view constants
const ADMIN_VIEW_AVAILABILITY = 'availability';
const ADMIN_VIEW_DETAILS = 'details';

const AdminContainer = styled.div`
  min-height: 100vh;
  max-height: 100vh;
  overflow-y: auto;
  background: #f8f9fa;
  padding: 20px;
`;

const LoginContainer = styled.div`
  max-width: 400px;
  margin: 100px auto;
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 16px;
  color: #000;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #2980b9;
  }

  &:disabled {
    background: #95a5a6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 14px;
  text-align: center;
`;

const AdminContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  overflow: hidden;
`;

const Header = styled.div`
  background: #2c3e50;
  color: white;
  padding: 24px;
  text-align: center;
  position: relative;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  margin: 8px 0 0 0;
  opacity: 0.9;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const StatusBadge = styled.span<{ variant: 'connected' | 'disconnected' | 'reconnecting' | 'static' }>`
  background: ${props => {
    switch (props.variant) {
      case 'connected': return 'rgba(39, 174, 96, 0.2)';
      case 'disconnected': return 'rgba(231, 76, 60, 0.2)';
      case 'reconnecting': return 'rgba(241, 196, 15, 0.2)';
      case 'static': return 'rgba(108, 117, 125, 0.2)';
      default: return 'rgba(108, 117, 125, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.variant) {
      case 'connected': return '#27ae60';
      case 'disconnected': return '#e74c3c';
      case 'reconnecting': return '#f39c12';
      case 'static': return '#6c757d';
      default: return '#6c757d';
    }
  }};
  padding: 4px 8px;
  border-radius: 4px;
  fontSize: 12px;
  font-weight: 500;
  animation: ${props => props.variant === 'reconnecting' ? 'pulse 1.5s ease-in-out infinite' : 'none'};

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const LogoutButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease;
  font-size: 14px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const Navigation = styled.div`
  display: flex;
  gap: 16px;
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid #e9ecef;
`;

const NavButton = styled.button<{ isActive: boolean }>`
  padding: 12px 24px;
  border: 1px solid ${props => props.isActive ? '#3498db' : '#e9ecef'};
  border-radius: 6px;
  background: ${props => props.isActive ? '#3498db' : 'white'};
  color: ${props => props.isActive ? 'white' : '#2c3e50'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3498db;
    background: ${props => props.isActive ? '#2980b9' : '#f8f9fa'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusInfo = styled.div`
  background: #f8f9fa;
  border-left: 4px solid #3498db;
  padding: 16px;
  margin: 16px 24px;
  border-radius: 0 6px 6px 0;
  font-size: 14px;
  color: #2c3e50;
`;

const LoadingContainer = styled.div`
  padding: 48px;
  text-align: center;
  color: #6c757d;
  font-size: 16px;
`;

const ApartmentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  padding: 24px;
`;

const ApartmentCard = styled.div<{ status: ApartmentStatus }>`
  background: white;
  border: 1px solid ${props => {
    switch (props.status) {
      case 'available': return '#27ae60';
      case 'reserved': return '#f39c12';
      case 'sold': return '#e74c3c';
      default: return '#27ae60';
    }
  }};
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
`;

const ApartmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ApartmentName = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
`;

const ApartmentStatusBadge = styled.span<{ status: ApartmentStatus }>`
  background: ${props => {
    switch (props.status) {
      case 'available': return '#27ae60';
      case 'reserved': return '#f39c12';
      case 'sold': return '#e74c3c';
      default: return '#27ae60';
    }
  }};
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const ApartmentDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DetailLabel = styled.span`
  font-weight: 500;
  color: #6c757d;
`;

const DetailValue = styled.span`
  font-weight: 600;
  color: #2c3e50;
`;

const StatusMessage = styled.div<{ variant: 'success' | 'error' | 'info' }>`
  background: ${props => 
    props.variant === 'success' ? 'rgba(39, 174, 96, 0.1)' :
    props.variant === 'error' ? 'rgba(231, 76, 60, 0.1)' :
    'rgba(52, 152, 219, 0.1)'
  };
  color: ${props => 
    props.variant === 'success' ? '#27ae60' :
    props.variant === 'error' ? '#e74c3c' :
    '#3498db'
  };
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  margin-top: 8px;
  text-align: center;
`;

const StatusDropdown = styled.select<{ status: ApartmentStatus }>`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => {
    switch (props.status) {
      case 'available': return '#27ae60';
      case 'reserved': return '#f39c12';
      case 'sold': return '#e74c3c';
      default: return '#27ae60';
    }
  }};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  color: ${props => {
    switch (props.status) {
      case 'available': return '#27ae60';
      case 'reserved': return '#f39c12';
      case 'sold': return '#e74c3c';
      default: return '#27ae60';
    }
  }};

  &:hover {
    background: ${props => {
      switch (props.status) {
        case 'available': return 'rgba(39, 174, 96, 0.05)';
        case 'reserved': return 'rgba(243, 156, 18, 0.05)';
        case 'sold': return 'rgba(231, 76, 60, 0.05)';
        default: return 'rgba(39, 174, 96, 0.05)';
      }
    }};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${props => {
      switch (props.status) {
        case 'available': return 'rgba(39, 174, 96, 0.2)';
        case 'reserved': return 'rgba(243, 156, 18, 0.2)';
        case 'sold': return 'rgba(231, 76, 60, 0.2)';
        default: return 'rgba(39, 174, 96, 0.2)';
      }
    }};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f5f5f5;
  }

  option {
    padding: 8px;
    font-weight: 500;
  }
`;

const PriceEditContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const PriceInput = styled.input`
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  color: #000;
  
  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
  }
`;

const PriceEditButton = styled.button<{ variant: 'save' | 'cancel' | 'edit' }>`
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
  
  ${props => {
    switch (props.variant) {
      case 'save':
        return `
          background: #27ae60;
          color: white;
          &:hover { background: #229954; }
        `;
      case 'cancel':
        return `
          background: #95a5a6;
          color: white;
          &:hover { background: #7f8c8d; }
        `;
      case 'edit':
        return `
          background: #3498db;
          color: white;
          &:hover { background: #2980b9; }
        `;
      default:
        return '';
    }
  }}
`;

const PriceDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AdminPage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [currentView, setCurrentView] = useState(ADMIN_VIEW_AVAILABILITY as any);
  const [updatingApartments, setUpdatingApartments] = useState<Set<number>>(new Set());
  const [editingPrices, setEditingPrices] = useState<Set<number>>(new Set());
  const [tempPrices, setTempPrices] = useState<{ [key: number]: string }>({});
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [successTimeout, setSuccessTimeout] = useState<NodeJS.Timeout | null>(null);
  
  const { 
    apartments, 
    loading, 
    error: apartmentError, 
    updateApartmentAvailability,
    updateApartment,
    useFirestore,
    connectionStatus,
    lastUpdateTime
  } = useApartments();

  const showSuccessMessage = (message: string) => {
    if (successTimeout) {
      clearTimeout(successTimeout);
    }
    setSuccessMessage(message);
    const timeout = setTimeout(() => {
      setSuccessMessage('');
    }, 4000);
    setSuccessTimeout(timeout);
  };

  // Helper functions for status management
  const getStatusText = (status: ApartmentStatus): string => {
    switch (status) {
      case 'available': return 'Verfügbar';
      case 'reserved': return 'Reserviert';
      case 'sold': return 'Verkauft';
      default: return 'Verfügbar';
    }
  };



  // Helper function to get apartment status (with backward compatibility)
  const getApartmentStatus = (apartment: any): ApartmentStatus => {
    if (apartment.status) {
      return apartment.status;
    }
    // Backward compatibility: convert available boolean to status
    return apartment.available !== false ? 'available' : 'sold';
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'Wildbach22') {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Falsches Passwort. Bitte versuchen Sie es erneut.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setError('');
  };

  const handleStatusChange = async (apartmentId: number, newStatus: ApartmentStatus) => {
    const apartment = apartments.find(apt => apt.id === apartmentId);
    if (apartment && !updatingApartments.has(apartmentId)) {
      setUpdatingApartments(prev => {
        const newSet = new Set(prev);
        newSet.add(apartmentId);
        return newSet;
      });
      
      try {
        // Update apartment with new status
        const updatedApartment = {
          ...apartment,
          status: newStatus,
          // For backward compatibility, also update available field
          available: newStatus === 'available'
        };
        
        await updateApartment(updatedApartment);
        
        const apartmentName = getCleanApartmentName(apartment.name);
        const statusText = getStatusText(newStatus);
        showSuccessMessage(`${apartmentName} wurde erfolgreich als "${statusText}" markiert.`);
        
        setTimeout(() => {
          setUpdatingApartments(prev => {
            const newSet = new Set(prev);
            newSet.delete(apartmentId);
            return newSet;
          });
        }, 500);
      } catch (err) {
        console.error('Failed to update status:', err);
        setUpdatingApartments(prev => {
          const newSet = new Set(prev);
          newSet.delete(apartmentId);
          return newSet;
        });
      }
    }
  };

  const handlePriceEdit = (apartmentId: number, currentPrice: number) => {
    setEditingPrices(prev => {
      const newSet = new Set(prev);
      newSet.add(apartmentId);
      return newSet;
    });
    setTempPrices(prev => ({ ...prev, [apartmentId]: currentPrice.toString() }));
  };

  const handlePriceCancel = (apartmentId: number) => {
    setEditingPrices(prev => {
      const newSet = new Set(prev);
      newSet.delete(apartmentId);
      return newSet;
    });
    setTempPrices(prev => {
      const newPrices = { ...prev };
      delete newPrices[apartmentId];
      return newPrices;
    });
  };

  const handlePriceSave = async (apartmentId: number) => {
    const newPrice = parseInt(tempPrices[apartmentId]);
    if (isNaN(newPrice) || newPrice <= 0) {
      alert('Bitte geben Sie einen gültigen Preis ein.');
      return;
    }

    setUpdatingApartments(prev => {
      const newSet = new Set(prev);
      newSet.add(apartmentId);
      return newSet;
    });

    try {
      const apartment = apartments.find(apt => apt.id === apartmentId);
      if (apartment) {
        await updateApartment({ ...apartment, price: newPrice });
        
        const apartmentName = getCleanApartmentName(apartment.name);
        showSuccessMessage(`Preis für ${apartmentName} wurde erfolgreich auf ${newPrice.toLocaleString()} CHF aktualisiert.`);
      }
      
      setEditingPrices(prev => {
        const newSet = new Set(prev);
        newSet.delete(apartmentId);
        return newSet;
      });
      setTempPrices(prev => {
        const newPrices = { ...prev };
        delete newPrices[apartmentId];
        return newPrices;
      });
    } catch (error) {
      console.error('Error updating apartment price:', error);
      alert('Fehler beim Aktualisieren des Preises. Bitte versuchen Sie es erneut.');
    } finally {
      setUpdatingApartments(prev => {
        const newSet = new Set(prev);
        newSet.delete(apartmentId);
        return newSet;
      });
    }
  };

  const getConnectionStatusText = () => {
    if (!useFirestore) return 'STATISCHE DATEN';
    
    switch (connectionStatus) {
      case 'connected': return 'LIVE VERBUNDEN';
      case 'reconnecting': return 'VERBINDE...';
      case 'disconnected': return 'VERBINDUNG UNTERBROCHEN';
      default: return 'UNBEKANNT';
    }
  };

  const getStatusVariant = (): 'connected' | 'disconnected' | 'reconnecting' | 'static' => {
    if (!useFirestore) return 'static';
    return connectionStatus;
  };

  const getCleanApartmentName = (name: string) => {
    // Robuste Extraktion des Apartment-Identifiers
    
    // Zuerst versuchen: "Wohnung A1 - ..." Pattern
    let match = name.match(/Wohnung ([AB]\d+)/);
    if (match) {
      return `Wohnung ${match[1]}`;
    }
    
    // Fallback: Falls nur "A1", "B5" etc.
    match = name.match(/^([AB]\d+)$/);
    if (match) {
      return `Wohnung ${match[1]}`;
    }
    
    // Fallback: Falls der Name bereits "A1" oder ähnlich ist, füge "Wohnung" hinzu
    match = name.match(/^([AB]\d+)(.*)$/);
    if (match) {
      return `Wohnung ${match[1]}`;
    }
    
    // Letzter Fallback: Originalname zurückgeben
    return name.startsWith('Wohnung') ? name : `Wohnung ${name}`;
  };

  if (!isAuthenticated) {
    return (
      <AdminContainer>
        <LoginContainer>
          <h2 style={{ textAlign: 'center', marginBottom: '24px', color: '#2c3e50' }}>
            Admin Zugang
          </h2>
          <LoginForm onSubmit={handleLogin}>
            <Input
              type="password"
              placeholder="Passwort eingeben"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Button type="submit">Anmelden</Button>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </LoginForm>
        </LoginContainer>
      </AdminContainer>
    );
  }

  if (currentView === ADMIN_VIEW_DETAILS) {
    return (
      <AdminContainer>
        <ApartmentDetailsManager onBack={() => setCurrentView(ADMIN_VIEW_AVAILABILITY)} />
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <AdminContent>
        <Header>
          <Title>Wohnungsverwaltung</Title>
          <Subtitle>
            Live-Verwaltung der 22 Wohnungen
          </Subtitle>
          
          <LogoutButton style={{ position: 'absolute', top: '20px', left: '20px', right: 'auto' }} onClick={() => window.location.href = '/'}>
            ← Zum Wohnungsfinder
          </LogoutButton>
          
          <LogoutButton onClick={handleLogout}>
            Abmelden
          </LogoutButton>
        </Header>
        
        {apartmentError && (
          <StatusMessage variant="error" style={{ margin: '16px 24px' }}>
            <AlertTriangle size={16} style={{ display: 'inline-block', marginRight: '8px' }} />
            {apartmentError}
          </StatusMessage>
        )}
        
        {successMessage && (
          <StatusMessage variant="success" style={{ margin: '16px 24px' }}>
            {successMessage}
          </StatusMessage>
        )}
        
        <Navigation>
          <NavButton
            isActive={currentView === ADMIN_VIEW_AVAILABILITY}
            onClick={() => setCurrentView(ADMIN_VIEW_AVAILABILITY)}
          >
            Verfügbarkeit verwalten
          </NavButton>
          <NavButton
            isActive={currentView === ADMIN_VIEW_DETAILS}
            onClick={() => setCurrentView(ADMIN_VIEW_DETAILS)}
          >
            Wohnungsdetails bearbeiten
          </NavButton>
        </Navigation>
        
        {loading ? (
          <LoadingContainer>
            Lade Wohnungsdaten...
          </LoadingContainer>
        ) : (
          <ApartmentGrid>
            {apartments.map((apartment) => {
              const isUpdating = updatingApartments.has(apartment.id);
              const currentStatus = getApartmentStatus(apartment);
              return (
                <ApartmentCard key={apartment.id} status={currentStatus}>
                  <ApartmentHeader>
                    <ApartmentName>{getCleanApartmentName(apartment.name)}</ApartmentName>
                    <ApartmentStatusBadge status={currentStatus}>
                      {getStatusText(currentStatus)}
                      {isUpdating && ' ⟳'}
                    </ApartmentStatusBadge>
                  </ApartmentHeader>
                  
                  <ApartmentDetails>
                    <DetailRow>
                      <DetailLabel>Preis:</DetailLabel>
                      {editingPrices.has(apartment.id) ? (
                        <PriceEditContainer>
                          <PriceInput
                            type="number"
                            value={tempPrices[apartment.id] || ''}
                            onChange={(e) => setTempPrices(prev => ({ ...prev, [apartment.id]: e.target.value }))}
                            placeholder="Preis eingeben"
                          />
                          <span style={{ fontSize: '14px', color: '#6c757d' }}>CHF</span>
                          <PriceEditButton variant="save" onClick={() => handlePriceSave(apartment.id)}>
                            ✓
                          </PriceEditButton>
                          <PriceEditButton variant="cancel" onClick={() => handlePriceCancel(apartment.id)}>
                            ✕
                          </PriceEditButton>
                        </PriceEditContainer>
                      ) : (
                        <PriceDisplayContainer>
                          <DetailValue>{formatPrice(apartment.price)}</DetailValue>
                          <PriceEditButton variant="edit" onClick={() => handlePriceEdit(apartment.id, apartment.price)}>
                            ✏️
                          </PriceEditButton>
                        </PriceDisplayContainer>
                      )}
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Größe:</DetailLabel>
                      <DetailValue>{apartment.size}m²</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Zimmer:</DetailLabel>
                      <DetailValue>{apartment.rooms}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>Etage:</DetailLabel>
                      <DetailValue>
                        {apartment.floor === 0 ? 'EG' : 
                         apartment.floor === 3 ? 'Attika' : 
                         `${apartment.floor}. OG`}
                      </DetailValue>
                    </DetailRow>
                  </ApartmentDetails>
                  
                  <StatusDropdown
                    status={currentStatus}
                    value={currentStatus}
                    onChange={(e) => handleStatusChange(apartment.id, e.target.value as ApartmentStatus)}
                    disabled={loading || isUpdating}
                    style={{
                      opacity: isUpdating ? 0.7 : 1,
                      cursor: isUpdating ? 'wait' : 'pointer'
                    }}
                  >
                    <option value="available">✅ Verfügbar</option>
                    <option value="reserved">🟡 Reserviert</option>
                    <option value="sold">🔴 Verkauft</option>
                  </StatusDropdown>
                </ApartmentCard>
              );
            })}
          </ApartmentGrid>
        )}
      </AdminContent>
    </AdminContainer>
  );
};

export default AdminPage; 